import React, { useState, useEffect } from 'react';

const GenerateAIModal = ({ isOpen, onClose, onGenerate, companySlug }) => {
  // Helper: append company_slug to API URLs for per-tab company scoping
  const scopeUrl = (url) => {
    if (!companySlug) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}company_slug=${companySlug}`;
  };
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchEvents();
    }
  }, [isOpen]);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch upcoming events that can be used for ads
      const response = await fetch('/api/events?upcoming=true', {
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || data || []);
        // Auto-select first event if available
        if ((data.events || data).length > 0) {
          setSelectedEventId((data.events || data)[0].id);
        }
      } else {
        setError('Failed to load events');
      }
    } catch (err) {
      setError('Error loading events: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedEventId) {
      setError('Please select an event');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const response = await fetch(scopeUrl('/api/meta_ads_ai/reports'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({ event_id: selectedEventId })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store generation state in session storage
        sessionStorage.setItem('metaAdsAI_generating', 'true');
        sessionStorage.setItem('metaAdsAI_report_id', data.report_id);
        sessionStorage.setItem('metaAdsAI_generation_start', Date.now().toString());

        onGenerate?.(data);
        onClose();

        // Trigger queue panel refresh
        if (window.__generationQueueRefresh) {
          window.__generationQueueRefresh();
        }
      } else {
        setError(data.error || 'Failed to start generation');
      }
    } catch (err) {
      setError('Error starting generation: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  const selectedEvent = events.find(e => e.id === selectedEventId);

  return (
    <div className="modal-overlay" onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="modal-container" onClick={e => e.stopPropagation()} style={{
        background: '#fff',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '560px',
        maxHeight: '80vh',
        overflow: 'hidden',
        boxShadow: '0 20px 50px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
              Generate AI Campaign
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
              Select an event to create AI-powered Meta Ads
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0 4px'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', maxHeight: '400px', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
              Loading events...
            </div>
          ) : events.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#6b7280', marginBottom: '16px' }}>
                No upcoming events found.
              </p>
              <p style={{ fontSize: '14px', color: '#9ca3af' }}>
                Create an event first to generate AI-powered ads.
              </p>
            </div>
          ) : (
            <>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Select Event
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {events.map(event => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEventId(event.id)}
                    style={{
                      padding: '14px 16px',
                      border: selectedEventId === event.id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: selectedEventId === event.id ? '#eff6ff' : '#fff',
                      transition: 'all 0.15s'
                    }}
                  >
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                      {event.name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {event.start_date && new Date(event.start_date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                      {event.venue_name && ` • ${event.venue_name}`}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {error && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Info Box */}
        {selectedEvent && (
          <div style={{
            margin: '0 24px 16px',
            padding: '12px 16px',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#166534'
          }}>
            <strong>AI will generate:</strong>
            <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
              <li>Marketing plan with audience analysis</li>
              <li>Campaign structure with ad sets</li>
              <li>Ad creatives with headlines & copy</li>
            </ul>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            disabled={generating}
            style={{
              padding: '10px 20px',
              background: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: generating ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating || !selectedEventId || loading}
            style={{
              padding: '10px 24px',
              background: generating || !selectedEventId ? '#9ca3af' : '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: generating || !selectedEventId ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {generating ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.416" strokeLinecap="round">
                    <animate attributeName="stroke-dashoffset" values="0;-62.83" dur="1s" repeatCount="indefinite"/>
                  </circle>
                </svg>
                Starting...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2L9.67 5.81L14 6.27L10.9 8.89L11.82 13L8 11.09L4.18 13L5.1 8.89L2 6.27L6.33 5.81L8 2Z"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                </svg>
                Generate with AI
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default GenerateAIModal;
